import { FC, createContext, useContext, useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import { API_ENDPOINTS } from "shared/constants";
import { dispatchGetSearchVenues } from "Venues/state";
import { IFilterCategories, ISortingOptionKeys } from "Venues/types";
import { useBandContext } from "./BandContext";

export enum VenueBlockState {
  "search",
  "chooseVenue", // existing venue chosen
  "createVenue",
  "editVenue",
}

export interface IVenueSearchQuery {
  query?: string;
  country?: string;
  page: number;
  sort: ISortingOptionKeys;
}

interface VenueBlockContextState {
  currentState: VenueBlockState;
  setCurrentState: (state: VenueBlockState) => void;
  query: IVenueSearchQuery;
  setQuery: (query: IVenueSearchQuery) => void;
  blockTitle: string | null;
  setBlockTitle: (title: string | null) => void;
  venueId: string | null;
  setVenueId: (venueId: string | null) => void;
  stageId: string | null;
  setStageId: (stageId: string | null) => void;
  capacityId: string | null;
  setCapacityId: (capacityId: string | null) => void;
}

const initialState: VenueBlockContextState = {
  currentState: VenueBlockState.search,
  setCurrentState: () => {},
  query: {
    query: "",
    country: "",
    page: 1,
    sort: ISortingOptionKeys.updatedAtDesc,
  },
  blockTitle: null,
  setBlockTitle: () => {},
  setQuery: () => {},
  venueId: null,
  setVenueId: () => {},
  stageId: null,
  setStageId: () => {},
  capacityId: null,
  setCapacityId: () => {},
};

const VenueBlockContext = createContext<VenueBlockContextState>(initialState);

interface VenueBlockProviderProps {
  defaultTitle?: string | null;
  defaultVenueId?: string | null;
  defaultStageId?: string | null;
  defaultCapacityId?: string | null;
}

export const VenueBlockProvider: FC<VenueBlockProviderProps> = ({
  defaultTitle = null,
  defaultVenueId = null,
  defaultStageId = null,
  defaultCapacityId = null,
  children,
}) => {
  const dispatch = useDispatch();
  const { bandId } = useBandContext();

  const [currentState, setCurrentState] = useState<VenueBlockState>(initialState.currentState);
  const [blockTitle, setBlockTitle] = useState<string | null>(defaultTitle);
  const [query, setQuery] = useState<IVenueSearchQuery>(initialState.query);

  const [venueId, setVenueId] = useState<string | null>(defaultVenueId);
  const [stageId, setStageId] = useState<string | null>(defaultStageId);
  const [capacityId, setCapacityId] = useState<string | null>(defaultCapacityId);

  const handleChangeVenueId = (venueId: string | null) => {
    setVenueId(venueId);
    setStageId(null);
    setCapacityId(null);
  };

  const handleChangeStageId = (stageId: string | null) => {
    setStageId(stageId);
    setCapacityId(null);
  };

  const fetchSearchVenues = useCallback(
    ({ page, sort, query, country }: IVenueSearchQuery) => {
      if (!bandId) return;

      const filters = {
        [IFilterCategories.title]: query,
        [IFilterCategories.country]: country,
      };

      if (query || country) {
        dispatchGetSearchVenues(dispatch, {
          filters,
          page,
          sort,
          band: API_ENDPOINTS.BAND(bandId),
        });
      }
    },
    [bandId, dispatch]
  );

  useEffect(() => {
    fetchSearchVenues(query);
  }, [query, fetchSearchVenues]);

  return (
    <VenueBlockContext.Provider
      value={{
        currentState,
        setCurrentState,
        query,
        setQuery,
        blockTitle,
        setBlockTitle,
        venueId,
        setVenueId: handleChangeVenueId,
        stageId,
        setStageId: handleChangeStageId,
        capacityId,
        setCapacityId,
      }}
    >
      {children}
    </VenueBlockContext.Provider>
  );
};

export const useVenueBlockContext = () => useContext(VenueBlockContext);
